import '@/index.css';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch'; // Polyfill for older browsers
import { notFound, RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { TopLevelErrorBoundary } from '@/components/TopLevelErrorBoundary';
import {
  reportCaughtError,
  reportRecoverableError,
  reportUncaughtError,
} from '@/lib/errorReporting';
import { ApiHttpError } from '@/lib/apiHttpError';
import { installAbortSignalTimeout } from '@/lib/abortSignalTimeout';
import {
  installChunkReloadHandler,
  isChunkLoadError,
  reloadForChunkError,
} from '@/lib/chunkReload';
import { SplashScreen } from '@/components/SplashScreen';
import { ChunkReloadSplashScreen } from '@/components/ChunkReloadSplashScreen';
import { initializeSentry, SentryProvider } from '@/lib/sentry';
import { EnrichAuthProvider, useEnrichAuth } from '@/providers/EnrichAuthProvider';
import { EnrichPostHogProvider } from '@/providers/EnrichPostHogProvider';
import { EnrichQueryClientProvider } from '@/providers/EnrichQueryClientProvider';
import { routeTree } from '@/routeTree.gen';
import NotFound from '@/components/pages/not-found/NotFound';
import { RouterContext } from '@/types/AppTypes';

installAbortSignalTimeout();

// Skip the View Transition for the initial hard-load route commit and for
// navigations to /chat. On iOS Safari, content streaming into the DOM right
// after the VT snapshot is captured (e.g. the ERIN greeting) fails to paint
// until the user scrolls, and Safari's Liquid Glass compositing can mis-report
// fixed-element rects during the transition (WebKit #275698, #320238).
let isInitialRouteCommit = true;
const supportsViewTransitionTypes =
  typeof window !== 'undefined' &&
  window.CSS?.supports?.('selector(:active-view-transition-type(a))');

// Create the router with View Transitions enabled
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // The internal-tools pane is its own scroll container (h-dvh overflow-auto),
  // so the router's per-navigation window reset never touches it; without this
  // its scrollTop would leak across route changes (e.g. Back from a scrolled
  // review detail rendering the queue mid-scroll).
  scrollToTopSelectors: ['#internal-tools'],
  defaultViewTransition: supportsViewTransitionTypes
    ? {
        types: ({ toLocation }) => {
          if (isInitialRouteCommit) {
            isInitialRouteCommit = false;
            return false;
          }
          if (toLocation.pathname.startsWith('/chat')) {
            return false;
          }
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return false;
          }
          return [];
        },
      }
    : false,
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: ({ error }) => {
    // Convert API 404 errors to router not found errors
    if (error instanceof ApiHttpError && error.response.status === 404) {
      throw notFound();
    }
    // A stale client's failed chunk import surfaces here; reload to recover.
    if (isChunkLoadError(error) && reloadForChunkError()) {
      return <ChunkReloadSplashScreen error={error} />;
    }
    throw error;
  },
  context: {
    auth: {
      isLoading: true,
      workOSUser: null,
      signIn: () => Promise.resolve(),
      signUp: () => Promise.resolve(),
    },
  },
});

// Initialize Sentry with TanStack Router integration AFTER router is created
initializeSentry(router);

// Auto-reload stale clients that fail to load a chunk after a deploy.
installChunkReloadHandler();

// Register the router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!, {
  onCaughtError: (error, errorInfo) => {
    reportCaughtError(error, errorInfo);
    console.error('Caught error in React tree:', error);
  },
  onUncaughtError: (error, errorInfo) => {
    reportUncaughtError(error, errorInfo);
    console.error('Uncaught error in React tree:', error);
  },
  onRecoverableError: (error, errorInfo) => {
    reportRecoverableError(error, errorInfo);
    console.error('Recoverable error in React tree:', error);
  },
}).render(
  <StrictMode>
    <SentryProvider>
      <TopLevelErrorBoundary>
        <EnrichPostHogProvider>
          <EnrichQueryClientProvider>
            <EnrichAuthProvider>
              <EnrichAuthOutlet />
            </EnrichAuthProvider>
          </EnrichQueryClientProvider>
        </EnrichPostHogProvider>
      </TopLevelErrorBoundary>
    </SentryProvider>
  </StrictMode>,
);

function EnrichAuthOutlet() {
  const { workOSUser, isLoading, workOSIsLoading, signIn, signUp } = useEnrichAuth();
  const prevAuthState = useRef({ isLoading, workOSUser });

  // Invalidate router when auth state changes to re-run beforeLoad hooks.
  // This is necessary because beforeLoad is not reactive to context changes -
  // it only runs during route transitions. When auth loading completes or
  // user logs in/out, we need to force the router to re-evaluate routes.
  useEffect(() => {
    const authChanged =
      prevAuthState.current.isLoading !== isLoading ||
      prevAuthState.current.workOSUser?.id !== workOSUser?.id;

    if (authChanged) {
      prevAuthState.current = { isLoading, workOSUser };
      // Force router to re-run beforeLoad hooks with new auth state
      router.invalidate();
    }
  }, [isLoading, workOSUser]);

  // Don't mount the router until the WorkOS session is resolved. Otherwise the
  // router eagerly resolves /_protected and fetches its whole component chunk
  // (providers, BottomNav, HomePage) before we know if the user is logged in —
  // then a logged-out redirect to WorkOS aborts those in-flight fetches, which
  // Firefox surfaces as "Failed to fetch dynamically imported module" errors.
  // Gating here means a logged-out user redirects with no protected chunk load.
  if (workOSIsLoading) {
    return <SplashScreen />;
  }

  const routerContext: RouterContext = {
    auth: {
      workOSUser,
      isLoading,
      signIn,
      signUp,
    },
  };

  return <RouterProvider router={router} context={routerContext} />;
}
